/**
 * Creates a promise which resolves after a given time (using timeout).
 *
 * @param ms milliseconds to wait
 * @returns a promise resolving after timeout
 */
export const wait = async (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Creates a Promise that is resolved (or rejected)
 * when the given promise is fulfilled (or rejected) or a given timelimit is elapsed.
 *
 * @param promise a given promise
 * @param ms timelimit in milliseconds
 * @returns a new promise
 */
export const timelimitPromise = <T>(promise: Promise<T>, ms: number = -1): Promise<T | void> => (ms > 0) ? Promise.race([wait(ms), promise]) : promise;

/**
 * Retries an operation with an given delay several times until it succeeds or the number of retries is reached.
 *
 * @param retries maximum number of retries
 * @param delay the delay between the retries
 * @param operation a async operation which resolves a value or throws
 * @returns a promised value or throws
 */
export const retry = async <T>(retries: number, delay: number, operation: () => Promise<T>): Promise<T> => {
    for (let i = 1; i <= retries; i++) {
        try {
            return await operation();
        } catch (error) {
            if (retries === i) throw error;
        }
        await wait(delay);
    }
    throw new Error('retry: failed');
}

/**
 * Polling for a variable several times with an given delay.
 *
 * @param retries maximum number of retries
 * @param delay the delay between the retries
 * @param resolver a function which lookups the variable, e.g. () => window.myVariable
 * @returns a promised variable or throws
 */
export const waitForVariable = <T>(retries: number, delay: number, resolver: () => T | undefined): Promise<T> => {
    return retry(retries, delay, async () => {
        const value = resolver();
        if (value === undefined) throw Error('variable not found');
        return value;
    });
}

/**
 * Wraps a synchronous function definition into an promise when executed.
 *
 * @example promisify((a: number) => a * 2)(8).then(result => console.log(result))
 */
export const promisify = <T extends (...args: any[]) => ReturnType<T>>(call: T) => {
    return (...args: Parameters<T>) => {
        return new Promise<ReturnType<T>>((resolve, reject) => {
            try {
                resolve(call(...args));
            } catch (e) {
                reject(e);
            }
        });
    }
}
